import type { JiraProjectSummary } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import type { JsmUseCaseProjectFailure } from './get-jsm-use-case-project-failure';

const LEASE_VERSION = 2;
const ACTIVE_LEASE_STALE_MS = 2 * 60 * 1_000;
const LEASE_HEARTBEAT_INTERVAL_MS = 10_000;
const TERMINAL_LEASE_EXPIRY_MS = 10 * 60 * 1_000;
const FOLLOWER_POLL_INTERVAL_MS = 1_000;
const FOLLOWER_PROJECT_LOOKUP_INTERVAL_MS = 5_000;
const STORAGE_PREFIX = 'wac:jsm-project-create';

type ActiveLease = {
	readonly attemptId: string;
	readonly createdAt: number;
	readonly expiresAt: number;
	readonly ownerId: string;
	readonly phase: 'creating' | 'preparing';
	readonly state: 'active';
	readonly updatedAt: number;
	readonly version: typeof LEASE_VERSION;
};

type SuccessLease = Omit<ActiveLease, 'state'> & {
	readonly project: JiraProjectSummary;
	readonly state: 'success';
};

type FailureLease = Omit<ActiveLease, 'state'> & {
	readonly failure: JsmUseCaseProjectFailure;
	readonly state: 'failure';
};

type ProjectCreationLease = ActiveLease | FailureLease | SuccessLease;

export type ProjectCreationLeaseHandle = {
	readonly attemptId: string;
	readonly ownerId: string;
	readonly role: 'follower' | 'owner';
	readonly storageKey: string | undefined;
};

export type ProjectCreationLeaseResult =
	| { readonly kind: 'cancelled' | 'expired' }
	| { readonly failure: JsmUseCaseProjectFailure; readonly kind: 'failure' }
	| { readonly kind: 'success'; readonly project: JiraProjectSummary };

export type ProjectCreationCoordinationResult =
	| { readonly kind: 'cancelled' }
	| {
			readonly attemptId: string;
			readonly failure: JsmUseCaseProjectFailure;
			readonly kind: 'failure';
	  }
	| {
			readonly attemptId: string;
			readonly kind: 'success';
			readonly project: JiraProjectSummary;
	  }
	| {
			readonly handle: ProjectCreationLeaseHandle;
			readonly kind: 'owner';
			readonly stopHeartbeat: () => void;
	  };

const createId = (): string => {
	try {
		return crypto.randomUUID();
	} catch {
		return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	}
};

const getStorageKey = (cloudId: string, projectKey: string): string =>
	`${STORAGE_PREFIX}:${encodeURIComponent(cloudId)}:${encodeURIComponent(projectKey)}`;

const isLeaseExpired = (lease: ProjectCreationLease): boolean =>
	lease.expiresAt <= Date.now() ||
	(lease.state === 'active' && lease.updatedAt + ACTIVE_LEASE_STALE_MS <= Date.now());

const hasValidLeaseIdentity = (value: Record<string, unknown>): boolean =>
	value['version'] === LEASE_VERSION &&
	typeof value['attemptId'] === 'string' &&
	typeof value['ownerId'] === 'string' &&
	typeof value['expiresAt'] === 'number';

const hasValidLeaseState = (value: Record<string, unknown>): boolean =>
	['creating', 'preparing'].includes(String(value['phase'])) &&
	['active', 'failure', 'success'].includes(String(value['state']));

const isProjectCreationLease = (value: unknown): value is ProjectCreationLease =>
	typeof value === 'object' &&
	value !== null &&
	hasValidLeaseIdentity(value as Record<string, unknown>) &&
	hasValidLeaseState(value as Record<string, unknown>);

const readLease = (storageKey: string): ProjectCreationLease | undefined => {
	try {
		const value: unknown = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
		if (!isProjectCreationLease(value)) {
			return undefined;
		}
		return value;
	} catch {
		return undefined;
	}
};

const claimFromStorage = (storageKey: string, ownerId: string): ProjectCreationLeaseHandle => {
	const existingLease = readLease(storageKey);
	if (existingLease && !isLeaseExpired(existingLease)) {
		return {
			attemptId: existingLease.attemptId,
			ownerId,
			role: 'follower',
			storageKey,
		};
	}

	const now = Date.now();
	const lease: ActiveLease = {
		attemptId: createId(),
		createdAt: now,
		expiresAt: now + ACTIVE_LEASE_STALE_MS,
		ownerId,
		phase: 'preparing',
		state: 'active',
		updatedAt: now,
		version: LEASE_VERSION,
	};
	localStorage.setItem(storageKey, JSON.stringify(lease));
	const verifiedLease = readLease(storageKey);
	return {
		attemptId: verifiedLease?.attemptId ?? lease.attemptId,
		ownerId,
		role: verifiedLease?.ownerId === ownerId ? 'owner' : 'follower',
		storageKey,
	};
};

const refreshProjectCreationLease = (handle: ProjectCreationLeaseHandle): void => {
	if (handle.role !== 'owner' || !handle.storageKey) {
		return;
	}
	try {
		const lease = readLease(handle.storageKey);
		if (
			!lease ||
			lease.state !== 'active' ||
			lease.attemptId !== handle.attemptId ||
			lease.ownerId !== handle.ownerId
		) {
			return;
		}
		const now = Date.now();
		localStorage.setItem(
			handle.storageKey,
			JSON.stringify({ ...lease, expiresAt: now + ACTIVE_LEASE_STALE_MS, updatedAt: now }),
		);
	} catch {
		// Creation continues with the hook's in-memory guard when storage becomes unavailable.
	}
};

const startProjectCreationLeaseHeartbeat = (handle: ProjectCreationLeaseHandle): (() => void) => {
	if (!handle.storageKey) {
		return () => undefined;
	}
	const heartbeatTimer = setInterval(
		() => refreshProjectCreationLease(handle),
		LEASE_HEARTBEAT_INTERVAL_MS,
	);
	return () => clearInterval(heartbeatTimer);
};

export const markProjectCreationLeaseCreating = (handle: ProjectCreationLeaseHandle): void => {
	if (handle.role !== 'owner' || !handle.storageKey) {
		return;
	}
	try {
		const lease = readLease(handle.storageKey);
		if (
			!lease ||
			lease.state !== 'active' ||
			lease.attemptId !== handle.attemptId ||
			lease.ownerId !== handle.ownerId
		) {
			return;
		}
		localStorage.setItem(
			handle.storageKey,
			JSON.stringify({ ...lease, phase: 'creating', updatedAt: Date.now() }),
		);
	} catch {
		// Creation continues with the hook's in-memory guard when storage becomes unavailable.
	}
};

export const abandonProjectCreationLease = (handle: ProjectCreationLeaseHandle): void => {
	if (handle.role !== 'owner' || !handle.storageKey) {
		return;
	}
	try {
		const lease = readLease(handle.storageKey);
		if (
			lease?.state === 'active' &&
			lease.phase === 'preparing' &&
			lease.attemptId === handle.attemptId &&
			lease.ownerId === handle.ownerId
		) {
			localStorage.removeItem(handle.storageKey);
		}
	} catch {
		// The active lease will expire if browser storage becomes unavailable during cleanup.
	}
};

export const claimProjectCreationLease = async ({
	cloudId,
	ownerId,
	projectKey,
}: {
	readonly cloudId: string;
	readonly ownerId: string;
	readonly projectKey: string;
}): Promise<ProjectCreationLeaseHandle> => {
	const storageKey = getStorageKey(cloudId, projectKey);
	try {
		if (navigator.locks) {
			return navigator.locks.request(storageKey, () => claimFromStorage(storageKey, ownerId));
		}
		return claimFromStorage(storageKey, ownerId);
	} catch {
		return { attemptId: createId(), ownerId, role: 'owner', storageKey: undefined };
	}
};

const writeTerminalLease = (
	handle: ProjectCreationLeaseHandle,
	terminal: Pick<FailureLease, 'failure'> | Pick<SuccessLease, 'project'>,
): void => {
	if (handle.role !== 'owner' || !handle.storageKey) {
		return;
	}
	try {
		const lease = readLease(handle.storageKey);
		if (!lease || lease.attemptId !== handle.attemptId || lease.ownerId !== handle.ownerId) {
			return;
		}
		localStorage.setItem(
			handle.storageKey,
			JSON.stringify({
				...lease,
				...terminal,
				expiresAt: Date.now() + TERMINAL_LEASE_EXPIRY_MS,
				state: 'project' in terminal ? 'success' : 'failure',
				updatedAt: Date.now(),
			}),
		);
	} catch {
		// Creation continues with the hook's in-memory guard when storage becomes unavailable.
	}
};

export const completeProjectCreationLease = (
	handle: ProjectCreationLeaseHandle,
	project: JiraProjectSummary,
): void => writeTerminalLease(handle, { project });

export const failProjectCreationLease = (
	handle: ProjectCreationLeaseHandle,
	failure: JsmUseCaseProjectFailure,
): void => writeTerminalLease(handle, { failure });

export const waitForProjectCreationLease = async ({
	handle,
	getProject,
	isCancelled,
	waitForNextCheck,
}: {
	readonly handle: ProjectCreationLeaseHandle;
	readonly getProject?: () => Promise<JiraProjectSummary | undefined>;
	readonly isCancelled: () => boolean;
	readonly waitForNextCheck: (delay: number) => Promise<void>;
}): Promise<ProjectCreationLeaseResult> => {
	if (!handle.storageKey) {
		return { kind: 'expired' };
	}

	let nextProjectLookupAt = 0;
	while (!isCancelled()) {
		const lease = readLease(handle.storageKey);
		if (!lease || lease.attemptId !== handle.attemptId || isLeaseExpired(lease)) {
			return { kind: 'expired' };
		}
		if (lease.state === 'success') {
			return { kind: 'success', project: lease.project };
		}
		if (lease.state === 'failure') {
			return { failure: lease.failure, kind: 'failure' };
		}
		if (getProject && Date.now() >= nextProjectLookupAt) {
			nextProjectLookupAt = Date.now() + FOLLOWER_PROJECT_LOOKUP_INTERVAL_MS;
			try {
				const project = await getProject();
				if (project) {
					return { kind: 'success', project };
				}
			} catch {
				// Jira may still be starting. Keep following the owner lease.
			}
		}
		await waitForNextCheck(FOLLOWER_POLL_INTERVAL_MS);
	}
	return { kind: 'cancelled' };
};

export const coordinateProjectCreation = async ({
	cloudId,
	getProject,
	isCancelled,
	ownerId,
	onFollower,
	projectKey,
	waitForNextCheck,
}: {
	readonly cloudId: string;
	readonly getProject?: () => Promise<JiraProjectSummary | undefined>;
	readonly isCancelled: () => boolean;
	readonly ownerId: string;
	readonly onFollower?: (handle: ProjectCreationLeaseHandle) => void;
	readonly projectKey: string;
	readonly waitForNextCheck: (delay: number) => Promise<void>;
}): Promise<ProjectCreationCoordinationResult> => {
	let hasReportedFollower = false;
	while (!isCancelled()) {
		const handle = await claimProjectCreationLease({ cloudId, ownerId, projectKey });
		if (handle.role === 'owner') {
			return { handle, kind: 'owner', stopHeartbeat: startProjectCreationLeaseHeartbeat(handle) };
		}
		if (!hasReportedFollower) {
			hasReportedFollower = true;
			onFollower?.(handle);
		}
		const result = await waitForProjectCreationLease({
			handle,
			getProject,
			isCancelled,
			waitForNextCheck,
		});
		if (result.kind === 'cancelled') {
			return { kind: 'cancelled' };
		}
		if (result.kind === 'success') {
			return { ...result, attemptId: handle.attemptId };
		}
		if (result.kind === 'failure') {
			return { ...result, attemptId: handle.attemptId };
		}
	}
	return { kind: 'cancelled' };
};

export const createProjectCreationOwnerId = createId;
